import React, { useMemo } from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { HelpCircle } from 'lucide-react';
import { TooltipProps } from 'recharts/types/component/Tooltip';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import type { UserDividendForecastEvent, UserDividendForecastMonth } from '../utils/userDividendForecast';

interface FutureDividendsProps {
    monthlyForecast: UserDividendForecastMonth[];
    next12mTotal: number;
    monthlyAverage: number;
    upcomingEvents?: UserDividendForecastEvent[];
    currencySymbol: string;
    horizonMonths: number;
    onHorizonChange: (months: number) => void;
}

const formatDividendAmount = (value: number) =>
    value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: value >= 1 ? 2 : 4,
    });

const FutureDividends: React.FC<FutureDividendsProps> = ({
    monthlyForecast,
    next12mTotal,
    upcomingEvents = [],
    currencySymbol,
    horizonMonths,
    onHorizonChange,
}) => {
    const horizonOptions = [
        { label: '3M', months: 3 },
        { label: '6M', months: 6 },
        { label: '12M', months: 12 },
    ];

    const data = useMemo(
        () => monthlyForecast.slice(0, Math.max(1, horizonMonths)).map((month) => ({
            name: month.label,
            total: month.amount,
            fullDate: month.monthKey,
            dividends: month.events,
        })),
        [monthlyForecast, horizonMonths]
    );

    const selectedHorizonMonths = Math.max(1, horizonMonths);
    const visibleTotal = selectedHorizonMonths === 12
        ? next12mTotal
        : data.reduce((sum, month) => sum + month.total, 0);
    const visibleMonthlyAverage = visibleTotal / selectedHorizonMonths;
    const hasFuturePaymentValues = visibleTotal > 0;
    const barSize = Math.max(8, Math.min(18, Math.floor(240 / (data.length || 1))));
    const upcomingPaymentEvents = upcomingEvents
        .filter((event) => event.dateType === 'PAYMENT_DATE')
        .slice(0, 4);

    const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (!active || !payload || !payload.length) return null;

        const monthData = payload[0].payload as (typeof data)[number];
        const hasDividends = monthData.dividends && monthData.dividends.length > 0;

        return (
            <div className="max-w-[280px] bg-[#1e1e26] border border-white/10 p-3 rounded-lg shadow-xl">
                <p className="text-slate-300 text-xs mb-1">{monthData.name}</p>
                <p className="text-white font-bold">
                    {currencySymbol}
                    {formatDividendAmount(monthData.total)}
                </p>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
                    {hasDividends ? (
                        monthData.dividends.map((event) => (
                            <div key={event.id} className="flex items-start justify-between gap-3 text-xs text-slate-200">
                                <span className="font-medium">{event.symbol}</span>
                                <span className="text-slate-300 text-right">
                                    {event.expectedCashBase !== null ? `${currencySymbol}${formatDividendAmount(event.expectedCashBase)}` : 'Amount TBA'}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="text-xs text-slate-500">No confirmed payments this month.</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-[340px] overflow-hidden rounded-2xl border border-white/5 bg-[#1e1e26] p-6 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mb-6 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center space-x-2">
                        <h2 className="truncate text-lg font-bold text-white">Future payments</h2>
                        <HelpCircle size={16} className="flex-shrink-0 text-slate-600 cursor-help" />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Confirmed + estimated dividend schedule</p>
                </div>
                <select
                    value={horizonMonths}
                    onChange={(event) => onHorizonChange(Number(event.target.value))}
                    className="flex-shrink-0 rounded-lg border border-white/10 bg-[#13131a] px-3 py-1.5 text-xs font-semibold text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                >
                    {horizonOptions.map((option) => (
                        <option key={option.months} value={option.months}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {!hasFuturePaymentValues ? (
                <div className="flex min-h-[230px] items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-5 text-center [html.theme-light_&]:border-slate-200 [html.theme-light_&]:bg-sky-50">
                    <div>
                        <p className="text-sm font-semibold text-slate-100 [html.theme-light_&]:text-slate-900">No future payments yet.</p>
                        <p className="mt-2 text-sm text-slate-500 [html.theme-light_&]:text-slate-600">We’ll show confirmed and estimated dividend schedules here when market data is available for your holdings.</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-3">
                            <div className="text-xs text-slate-400">Projected next {selectedHorizonMonths}m</div>
                            <div className="mt-1 truncate text-xl font-bold text-white">
                                {currencySymbol}{formatDividendAmount(visibleTotal)}
                            </div>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                            <div className="text-xs text-slate-400">Monthly average</div>
                            <div className="mt-1 truncate text-xl font-bold text-white">
                                {currencySymbol}{formatDividendAmount(visibleMonthlyAverage)}
                            </div>
                        </div>
                    </div>

                    <div className="h-48 w-full min-w-0 overflow-hidden">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data} margin={{ top: 20, right: 4, left: 4, bottom: 0 }}>
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }} allowEscapeViewBox={{ x: true, y: true }} />
                                <Bar dataKey="total" radius={[4, 4, 0, 0]} barSize={barSize}>
                                    {data.map((_, index) => <Cell key={`confirmed-${index}`} fill="#0EA5E9" />)}
                                    <LabelList dataKey="total" position="top" formatter={(val: number) => (val > 0 ? `${currencySymbol}${formatDividendAmount(val)}` : '')} style={{ fill: '#94a3b8', fontSize: 11, fontWeight: 500 }} offset={10} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {upcomingPaymentEvents.length > 0 && (
                        <div className="mt-5 space-y-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Upcoming payment events</div>
                            {upcomingPaymentEvents.map((event) => (
                                <div key={event.id} className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
                                    <span className="truncate text-slate-200">{event.symbol} · {event.name}</span>
                                    <span className="text-emerald-400">
                                        {event.expectedCashBase !== null ? `${currencySymbol}${formatDividendAmount(event.expectedCashBase)}` : 'Amount TBA'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FutureDividends;
